////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

/**
 * ════════════════════════════════════════════════════════════════════════════════
 * AUTH MAIN APPLICATION ROUTER - THE AUTHENTICATION FLOW ORCHESTRATOR
 * ════════════════════════════════════════════════════════════════════════════════
 *
 * ARCHITECTURAL ROLE: This is the root React application that orchestrates the
 * complete authentication user journey through React Router, providing client-side
 * routing for sign-in, registration, password reset, and payment flows.
 *
 * I[R{DVT}]O PATTERN ANALYSIS:
 *
 * INPUT (I):
 *   - Browser URL navigation (user-initiated or programmatic)
 *   - Route parameters from URL path
 *   - Browser history state (back/forward navigation)
 *   - Initial page load from auth frontend server
 *
 * REQUIREMENTS (R):
 *
 *   DEFINITIONS (D):
 *     - BrowserRouter: Client-side routing configuration with URL paths
 *     - Route mappings: URL path → React component associations
 *     - Navigate redirects: Automatic routing for default and invalid paths
 *     - Pod components: Page-level orchestrators (sign-in, register, etc.)
 *
 *   VALIDATIONS (V):
 *     - Route existence validation (catch-all for 404 handling)
 *     - Authentication state validation (redirect to sign-in if needed)
 *     - Path parameter validation (future implementation)
 *
 *   TRANSFORMATIONS (T):
 *     - URL path to component rendering
 *     - Default route ("/") to sign-in redirect
 *     - Invalid routes ("*") to sign-in redirect
 *     - Browser history management for navigation
 *
 * OUTPUT (O):
 *   - Rendered React components based on current route
 *   - Browser URL updates reflecting navigation state
 *   - HTML mounted to DOM root element
 *
 * USER JOURNEY FLOW:
 * 1. User visits / → Redirects to /auth/sign-in
 * 2. User visits /auth/sign-in → Shows sign-in form
 * 3. User clicks "Sign up" → Navigates to /auth/register
 * 4. User clicks "Forgot password?" → Navigates to /auth/forget-password
 * 5. After registration → Can navigate to /auth/payment for subscription
 * 6. Invalid URLs → Redirect to /auth/sign-in
 *
 * ARCHITECTURAL PATTERN: Pod/Pea Component Architecture
 * - Pods: Page-level orchestrators with state management and API calls
 * - Peas: Presentation components with UI and event handlers
 * - This main file only handles routing, not authentication logic
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import Pod_Lobby_SignIn_SRC_Auth_Customer_Kings from '../sign-in/lobby/pod/Bitsbound_Kings_Customer_Auth_SRC_SignIn_Lobby_Pod.js';
import Pod_Lobby_Register_SRC_Auth_Customer_Kings from '../register/lobby/pod/Bitsbound_Kings_Customer_Auth_SRC_Register_Lobby_Pod.js';
import Pod_Lobby_ForgetPassword_SRC_Auth_Customer_Kings from '../forget-password/lobby/pod/Bitsbound_Kings_Customer_Auth_SRC_ForgetPassword_Lobby_Pod.js';
import Pod_Lobby_Payment_SRC_Auth_Customer_Kings from '../payment/lobby/pod/Bitsbound_Kings_Customer_Auth_SRC_Payment_Lobby_Pod.js';

/**
 * DEFINITION (D): Browser router configuration defining all authentication routes
 * Maps URL paths to React components following the authentication user journey
 *
 * VALIDATION: Includes catch-all route (*) to handle invalid URLs
 * TRANSFORMATION: Navigate components perform automatic redirects
 */
const router = createBrowserRouter([
  {
    path: "/",  // DEFINITION: Root path (default entry point)
    element: <Navigate to="/auth/sign-in" replace />  // TRANSFORMATION: Redirect to sign-in
  },
  {
    path: "/auth/sign-in",  // DEFINITION: Sign-in page route
    element: <Pod_Lobby_SignIn_SRC_Auth_Customer_Kings />  // OUTPUT: Render sign-in Pod component
  },
  {
    path: "/auth/register",  // DEFINITION: Registration page route
    element: <Pod_Lobby_Register_SRC_Auth_Customer_Kings />  // OUTPUT: Render registration Pod component
  },
  {
    path: "/auth/forget-password",  // DEFINITION: Password reset page route
    element: <Pod_Lobby_ForgetPassword_SRC_Auth_Customer_Kings />  // OUTPUT: Render password reset Pod component
  },
  {
    path: "/auth/payment",  // DEFINITION: Payment/subscription page route
    element: <Pod_Lobby_Payment_SRC_Auth_Customer_Kings />  // OUTPUT: Render payment Pod component
  },
  {
    path: "*",  // VALIDATION: Catch-all route for 404 handling
    element: <Navigate to="/auth/sign-in" replace />  // TRANSFORMATION: Redirect invalid URLs to sign-in
  }
]);

/**
 * TRANSFORMATION (T): Root application component providing router context
 * Wraps RouterProvider to enable React Router navigation throughout the app
 *
 * INPUT: Browser URL state
 * OUTPUT: Rendered route component based on current URL
 */
const App: React.FC = () => {
  return (
    <div>
      {/* TRANSFORMATION: RouterProvider enables declarative routing */}
      <RouterProvider router={router} />
      {/* ANALYTICS: Vercel Analytics and Speed Insights for production monitoring */}
      <Analytics />
      <SpeedInsights />
    </div>
  );
};

/**
 * TRANSFORMATION (T): Application bootstrap - mounts React app to DOM
 * Implements React 18 concurrent rendering with createRoot API
 *
 * INPUT: HTML element with id="root"
 * OUTPUT: Mounted React application with router enabled
 */
// VALIDATION: Ensure root element exists in HTML
const rootElement = document.getElementById('root');
if (!rootElement) {
  // VALIDATION: Fail-fast if root element missing (prevents silent failures)
  throw new Error('Root element not found - ensure index.html contains <div id="root"></div>');
}

// TRANSFORMATION: Create React 18 root with concurrent rendering
const root = ReactDOM.createRoot(rootElement);

// OUTPUT: Render application with StrictMode for development warnings
root.render(
  <React.StrictMode>
    {/* StrictMode enables additional development checks and warnings */}
    <App />
  </React.StrictMode>
);

export default App;